import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import api from '../../../services/api';
import { 
  EyeIcon, 
  TrashIcon, 
  EnvelopeIcon, 
  EnvelopeOpenIcon,
  ArchiveBoxIcon,
  ChatBubbleLeftRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import Pagination from '../../../components/common/Pagination';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import ContactMessageFilter from './ContactMessageFilter';
import { toast } from 'react-toastify';

const ContactMessageList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    sort_by: 'created_at',
    sort_order: 'desc'
  });
  const queryClient = useQueryClient();

  // Fetch contact messages
  const { data, isLoading, isError, error } = useQuery(
    ['contactMessages', currentPage, filters],
    async () => {
      try {
        console.log('Fetching contact messages with params:', { page: currentPage, ...filters });
        // Fix the API endpoint to match the backend route
        const response = await api.get('/api/v1/admin/contact-messages', {
          params: {
            page: currentPage,
            per_page: 10,
            ...filters
          }
        });
        console.log('API Response:', response.data);
        // Log the exact structure of the response data
        console.log('Response data structure:', JSON.stringify(response.data, null, 2));
        console.log('Data property type:', typeof response.data.data);
        console.log('Data property keys:', response.data.data ? Object.keys(response.data.data) : 'No data property');
        
        if (response.data.data && response.data.data.data) {
          console.log('Nested data array length:', response.data.data.data.length);
        }
        
        return response.data;
      } catch (err) {
        console.error('Error fetching messages:', err);
        throw err;
      }
    },
    {
      keepPreviousData: true,
      staleTime: 5000,
      onError: (err) => {
        toast.error(`Failed to load messages: ${err.message}`);
      }
    }
  );

  // Mark as read mutation
  const markAsReadMutation = useMutation(
    (id) => api.put(`/api/v1/admin/contact-messages/${id}/mark-as-read`, {}),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['contactMessages']);
        toast.success('Message marked as read');
      },
      onError: (err) => {
        toast.error(`Failed to mark as read: ${err.message}`);
      }
    }
  );

  // Mark as replied mutation
  const markAsRepliedMutation = useMutation(
    (id) => api.put(`/api/v1/admin/contact-messages/${id}/mark-as-replied`, {}),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['contactMessages']);
        toast.success('Message marked as replied');
      },
      onError: (err) => {
        toast.error(`Failed to mark as replied: ${err.message}`);
      }
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    (id) => api.delete(`/api/v1/admin/contact-messages/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['contactMessages']);
        toast.success('Message deleted successfully');
      },
      onError: (err) => {
        toast.error(`Failed to delete message: ${err.message}`);
      }
    }
  );

  // Update status mutation
  const updateStatusMutation = useMutation(
    ({ id, status }) => api.put(`/api/v1/admin/contact-messages/${id}`, { status }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['contactMessages']);
        toast.success('Message status updated');
      },
      onError: (err) => {
        toast.error(`Failed to update status: ${err.message}`);
      }
    }
  );

  const handleMarkAsRead = (id) => {
    markAsReadMutation.mutate(id);
  };

  const handleMarkAsReplied = (id) => {
    markAsRepliedMutation.mutate(id);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleArchive = (id) => {
    updateStatusMutation.mutate({ id, status: 'archived' });
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      ...newFilters
    }));
    setCurrentPage(1);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800';
      case 'read':
        return 'bg-green-100 text-green-800';
      case 'replied':
        return 'bg-purple-100 text-purple-800';
      case 'archived':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return <div className="text-red-500">Error: {error.message}</div>;
  }
  
  // Based on the logs, the API response structure is:
  // { 
  //   success: true, 
  //   data: { 
  //     current_page: 1, 
  //     data: [array of message objects], 
  //     ... pagination info ...
  //   }, 
  //   message: '...' 
  // }
  
  // Direct access to the messages array based on the response structure
  // The direct check shows the messages are in data.data.data
  const apiData = data?.data;
  const messages = apiData?.data || [];
  const pagination = apiData;
  
  // Log the extracted data for debugging
  console.log('Pagination data:', pagination);
  console.log('Extracted messages:', messages);
  
  // Add a direct check to see what's in the data structure
  if (data?.data?.data && Array.isArray(data.data.data)) {
    console.log('DIRECT CHECK - Found array in data.data.data with length:', data.data.data.length);
    // Override the messages array with the direct data
    const directMessages = data.data.data;
    
    // Define handlePageChange function for the direct rendering
    const handlePageChange = (page) => {
      setCurrentPage(page);
    };
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Contact Messages</h1>
          <div className="flex space-x-4">
            <ContactMessageFilter onFilterChange={handleFilterChange} />
          </div>
        </div>

        {directMessages.length === 0 ? (
          <div className="text-center py-10">
            <EnvelopeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No messages</h3>
            <p className="mt-1 text-sm text-gray-500">
              There are no contact messages matching your criteria.
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sender
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Subject
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {directMessages.map((message) => (
                    <tr key={message.id} className={message.status === 'new' ? 'bg-blue-50' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{message.name}</div>
                            <div className="text-sm text-gray-500">{message.email}</div>
                            {message.phone && (
                              <div className="text-sm text-gray-500">{message.phone}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">{message.subject}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          message.status === 'new' ? 'bg-blue-100 text-blue-800' :
                          message.status === 'read' ? 'bg-yellow-100 text-yellow-800' :
                          message.status === 'replied' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {message.status.charAt(0).toUpperCase() + message.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(message.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <Link
                            to={`/admin/contact-messages/${message.id}`}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            View
                          </Link>
                          <button
                            onClick={() => handleMarkAsRead(message.id)}
                            className="text-yellow-600 hover:text-yellow-900"
                            disabled={message.status !== 'new'}
                          >
                            Mark as Read
                          </button>
                          <button
                            onClick={() => handleDelete(message.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pagination && pagination.last_page > 1 && (
              <div className="flex justify-center mt-6">
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    <span className="sr-only">Previous</span>
                    <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                  {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium ${
                        page === currentPage
                          ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                          : 'text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === pagination.last_page}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    <span className="sr-only">Next</span>
                    <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </nav>
              </div>
            )}
          </>
        )}
      </div>
    );
  } else {
    console.log('DIRECT CHECK - data.data.data is not an array or is empty');
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Contact Messages</h1>
        <div className="mt-3 sm:mt-0">
          <ContactMessageFilter onFilterChange={handleFilterChange} />
        </div>
      </div>

      {messages.length === 0 ? (
        <div className="text-center py-10">
          <EnvelopeIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No messages</h3>
          <p className="mt-1 text-sm text-gray-500">
            There are no contact messages matching your criteria.
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sender
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subject
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {messages.map((message) => (
                  <tr key={message.id} className={message.status === 'new' ? 'bg-blue-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{message.name}</div>
                          <div className="text-sm text-gray-500">{message.email}</div>
                          {message.phone && (
                            <div className="text-sm text-gray-500">{message.phone}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate">{message.subject}</div>
                      <div className="text-sm text-gray-500 max-w-xs truncate">{message.message}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(message.status)}`}>
                        {message.status.charAt(0).toUpperCase() + message.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(message.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <Link
                          to={`/admin/contact-messages/${message.id}`}
                          className="text-primary hover:text-primary-dark"
                          title="View Details"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </Link>
                        
                        {message.status === 'new' && (
                          <button
                            onClick={() => handleMarkAsRead(message.id)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Mark as Read"
                          >
                            <EnvelopeOpenIcon className="h-5 w-5" />
                          </button>
                        )}
                        
                        {(message.status === 'new' || message.status === 'read') && (
                          <button
                            onClick={() => handleMarkAsReplied(message.id)}
                            className="text-purple-600 hover:text-purple-900"
                            title="Mark as Replied"
                          >
                            <ChatBubbleLeftRightIcon className="h-5 w-5" />
                          </button>
                        )}
                        
                        {message.status !== 'archived' && (
                          <button
                            onClick={() => handleArchive(message.id)}
                            className="text-gray-600 hover:text-gray-900"
                            title="Archive"
                          >
                            <ArchiveBoxIcon className="h-5 w-5" />
                          </button>
                        )}
                        
                        <button
                          onClick={() => handleDelete(message.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination && (
            <Pagination
              currentPage={currentPage}
              totalPages={pagination.last_page || 1}
              onPageChange={setCurrentPage}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ContactMessageList;
